import { useState } from 'react';
import { getProjectCoverAssets } from './project-cover-assets';
import './ProjectCoverMedia.css';

// ─── Tipos ────────────────────────────────────────────────────────────────────

interface ProjectCoverMediaProps {
  slug: string;
  coverVideoUrl: string | null;
  coverPosterUrl: string | null;
  coverImageUrl: string | null;
}

type MediaStage = 'video' | 'image' | 'none';

// ─── Sub-componentes internos ─────────────────────────────────────────────────

function CoverOverlay(): JSX.Element {
  return (
    <>
      <div className="pcm-overlay" aria-hidden="true" />
      <div className="pcm-vignette" aria-hidden="true" />
    </>
  );
}

interface CoverVideoProps {
  videoSrc: string;
  posterSrc: string;
  onError: () => void;
}

function CoverVideo({ videoSrc, posterSrc, onError }: CoverVideoProps): JSX.Element {
  return (
    <div className="pcm">
      <video
        className="pcm-video"
        autoPlay
        muted
        loop
        playsInline
        poster={posterSrc}
        onError={onError}
        aria-hidden="true"
      >
        <source src={videoSrc} type="video/mp4" />
      </video>
      <CoverOverlay />
    </div>
  );
}

interface CoverImageProps {
  imageSrcs: string[];
  onAllFailed: () => void;
}

function CoverImage({ imageSrcs, onAllFailed }: CoverImageProps): JSX.Element {
  const [idx, setIdx] = useState(0);

  function handleError(): void {
    if (idx + 1 < imageSrcs.length) {
      setIdx(i => i + 1);
    } else {
      onAllFailed();
    }
  }

  const src = imageSrcs[idx];
  if (!src) return <></>;

  return (
    <div className="pcm">
      <img className="pcm-image" src={src} alt="" aria-hidden="true" onError={handleError} />
      <CoverOverlay />
    </div>
  );
}

// ─── Componente principal ─────────────────────────────────────────────────────

/**
 * Renderiza el fondo de portada del proyecto.
 * Prioridad: video (DTO → local) → imagen (DTO → local webp → local jpg) → nada.
 * Si todo falla, no renderiza nada — el mapa territorial queda visible como fondo.
 */
export function ProjectCoverMedia({
  slug,
  coverVideoUrl,
  coverPosterUrl,
  coverImageUrl,
}: ProjectCoverMediaProps): JSX.Element {
  const local = getProjectCoverAssets(slug);
  const [stage, setStage] = useState<MediaStage>('video');

  const videoSrc = coverVideoUrl ?? local.videoUrl;
  const posterSrc = coverPosterUrl ?? local.posterUrl;

  const imageSrcs = [coverImageUrl, local.webpUrl, local.jpgUrl].filter(
    (s): s is string => s !== null
  );

  if (stage === 'video') {
    return (
      <CoverVideo videoSrc={videoSrc} posterSrc={posterSrc} onError={() => setStage('image')} />
    );
  }

  if (stage === 'image' && imageSrcs.length > 0) {
    return <CoverImage imageSrcs={imageSrcs} onAllFailed={() => setStage('none')} />;
  }

  return <></>;
}
